import React from 'react';
import ActionButton from '../../components/ActionButton';
import {
  dueLine, laterLine, fillPercent, nextLabel, summaryLine, deskWhoLine, deskOwnLine, deskShelfLine, flagWords,
  NEXT_UP, NOBODY_ON_IT, coatedNote, RECOUNT_LABEL,
} from '../../lib/weldingWords';

// #746 - THE MANAGER'S DESK (gate 746, F6), Session 79.22b.6.4. From 1024 wide:
// the next-up card (the list's first size), the summary line, and the table with
// a fill bar per row - Direction C's desk table in Direction B's style. BUILT
// EXACTLY AS GROK DREW IT (746 D25): every size, space and colour in
// welding.css's desk block is F6's
// (docs/gates/drawings/2026-10-10_welding_746_amended_board_grok.html).
//
// The numbers are the board's own - weldingDemand(), the same sizes in the same
// order (746 D33) - and every sentence is weldingWords.js's, from the copy table.
// The page (WeldingPage.js) owns the reads, the answer bar and the size's
// window; this file only draws.
//
// NOT HERE, by plan: the Reserves button in the header (79.22b.6.4b, R1).

// A number cell, as F6 writes it: Later always quiet; Top-up and Reserve a quiet
// dash at 0; the rest as numbers, 0 included.
function Num({ n, quiet = false, dashAtZero = false }) {
  const v = Number(n) || 0;
  if (dashAtZero && v === 0) return <span className="n dim">—</span>;
  return <span className={`n${quiet ? ' dim' : ''}`}>{v}</span>;
}

// Who is on a size, for a desk row and the next-up card: your own flag (with
// Stop) or everyone else's, oldest first (flagsShown()'s order).
function splitFlags(flags, myId) {
  const list = Array.isArray(flags) ? flags : [];
  const mine = list.find(f => myId != null && String(f.user_id) === String(myId)) || null;
  return { mine, others: list.filter(f => f !== mine) };
}

// The buttons a row and the next-up card carry (F6): Help on this while someone
// else is on the size and you are not; Clear flag for each person whose flag
// you may clear (canClearWeldFlag; 79.22b.6.4 R1 D2 - one per person, so a
// manager ends exactly the forgotten flag); Stop on your own; Record always.
function SizeButtons({ size, mine, others, mayClear, busyKey, onRecord, onHelp, onClear, onStop, clearBelow }) {
  return (
    <div className="wp-dbtns">
      {mine && <ActionButton className="wp-btn" busy={busyKey === `stop`} onClick={onStop}>Stop</ActionButton>}
      {!mine && others.length > 0 && (
        <ActionButton className="wp-btn" busy={busyKey === `help-${size.itemId}`} onClick={() => onHelp(size)}>Help on this</ActionButton>
      )}
      {mayClear && !clearBelow && others.map(f => (
        <ActionButton key={f.id} className="wp-btn" aria-label={`Clear ${f.user_name || 'this'}'s flag`}
          busy={busyKey === `clear-${f.id}`} onClick={() => onClear(f)}>Clear flag</ActionButton>
      ))}
      <button type="button" className="wp-btn pri" onClick={() => onRecord(size)}>Record</button>
    </div>
  );
}

// TWO OR MORE PEOPLE ON A SIZE, for someone who may clear (C3, approved): each
// name with its own Clear flag on a line of its own under the row's numbers, so
// nobody can press the wrong one. One person keeps F6's row (C5).
function ClearPairs({ others, busyKey, onClear }) {
  return others.map(f => (
    <div key={f.id} className="wp-dpair">
      <span className="wp-dwho">{deskWhoLine(f)}</span>
      <ActionButton className="wp-btn" aria-label={`Clear ${f.user_name || 'this'}'s flag`}
        busy={busyKey === `clear-${f.id}`} onClick={() => onClear(f)}>Clear flag</ActionButton>
    </div>
  ));
}

// ONE ROW OF THE TABLE. Module-level (locked rule 2).
function DeskRow({ size, horizonDays, noDateColumn, flags, myId, mayClear, busyKey, onRecord, onHelp, onClear, onStop, onCount, countReason }) {
  const { mine, others } = splitFlags(flags, myId);
  const pairs = mayClear && others.length > 1;
  const pct = fillPercent(size);
  const shelf = deskShelfLine(size.shelf);
  // The Later line under a Covered size's name says why it is listed at all -
  // and so, at 79.22b.6.4.1, under a size whose orders are all Later (the
  // undrawn edge "only later orders").
  const later = size.state === 'covered' || size.edge === 'later' ? laterLine(size.later) : '';
  return (
    <div className="wp-dtr">
      <div className="wp-dsz">
        <span className="wp-dszline">
          {size.name}
          {/* F6 draws Covered in a row, and Late only on the next-up card. */}
          {size.state === 'covered' && <span className="wp-chip cov">Covered</span>}
        </span>
        {mine && <span className="wp-dwho">{deskOwnLine(mine)}</span>}
        {!pairs && others.map(f => <span key={f.id} className="wp-dwho">{deskWhoLine(f)}</span>)}
        {shelf && <span className="wp-drsub">{shelf}</span>}
        {later && <span className="wp-drsub">{later}</span>}
        <span className="wp-gauge" aria-hidden="true">{pct > 0 && <span style={{ width: `${pct}%` }} />}</span>
      </div>
      <Num n={size.late} />
      <Num n={size.today} />
      <Num n={size.next} />
      {noDateColumn && <Num n={size.noDate} />}
      <Num n={size.later} quiet />
      <Num n={size.shelfTopUp} quiet={!(size.shelfTopUp > 0)} dashAtZero />
      <Num n={size.reserve} quiet={!(size.reserve > 0)} dashAtZero />
      <Num n={size.waitingShown} />
      <span className="n"><b>{size.stillToWeld}</b></span>
      <SizeButtons size={size} mine={mine} others={others} mayClear={mayClear} busyKey={busyKey}
        onRecord={onRecord} onHelp={onHelp} onClear={onClear} onStop={onStop} clearBelow={pairs} />
      {pairs && <ClearPairs others={others} busyKey={busyKey} onClear={onClear} />}
      {/* S7 on a desk (79.22b.6.4.1; undrawn there, built from C3's line under
          the row): the note in #791's words, and Recount welded frames, which
          opens the size's window straight onto the count. */}
      {size.belowZero && (
        <div className="wp-dpair wp-dcoated">
          <span className="wp-dwho">{coatedNote(size)}</span>
          <ActionButton className="wp-btn pri" blockedReason={countReason} onClick={() => onCount(size)}>{RECOUNT_LABEL}</ActionButton>
        </div>
      )}
    </div>
  );
}

// THE NEXT-UP CARD: the list's first size still to weld, with its due line, its
// two numbers, its bar and who is on it.
function NextUp({ size, horizonDays, flags, myId, mayClear, busyKey, onRecord, onHelp, onClear, onStop }) {
  const { mine, others } = splitFlags(flags, myId);
  const pairs = mayClear && others.length > 1;
  const pct = fillPercent(size);
  // One person: the row's own words. Two or more: the copy table's line,
  // "Owen Pike and Tess Ivory are on it" (Grok's comparison, findings 1-2), with
  // each person's Clear flag under the card for someone who may clear.
  const who = mine ? deskOwnLine(mine)
    : others.length === 1 ? deskWhoLine(others[0])
      : others.length ? flagWords(others, myId).text : '';
  return (
    <section className="wp-dhero" aria-label={NEXT_UP}>
      <div className="wp-dtop">
        <div>
          <div className="wp-kicker"><span className="wp-kick">{NEXT_UP}</span>{size.lateChip && <span className="wp-chip late">Late</span>}</div>
          <h3>{size.name}</h3>
          <p className="wp-dfacts">{dueLine(size, horizonDays) || 'Nothing on order'}</p>
        </div>
        <div className="wp-dnums">
          <div><b>{size.stillToWeld}</b><span>Still to weld</span></div>
          <div><b>{size.waitingShown}</b><span>Welded, waiting</span></div>
        </div>
      </div>
      <div className="wp-gauge" aria-hidden="true">{pct > 0 && <span style={{ width: `${pct}%` }} />}</div>
      <div className={`wp-dact${who ? ' on' : ''}`}>
        <p>{who || NOBODY_ON_IT}</p>
        <SizeButtons size={size} mine={mine} others={others} mayClear={mayClear} busyKey={busyKey}
          onRecord={onRecord} onHelp={onHelp} onClear={onClear} onStop={onStop} clearBelow={pairs} />
      </div>
      {pairs && <ClearPairs others={others} busyKey={busyKey} onClear={onClear} />}
    </section>
  );
}

// THE DESK'S LIST: next-up card, summary line and table. `head` is the page's
// header (title, the line under it or the branch chip, and the total).
export default function WeldingDesk({
  sizes, horizonDays, flagMap, myId, mayClear, busyKey, notes, onRecord, onHelp, onClear, onStop, onCount, countReason,
}) {
  const next = sizes.find(s => s.state === 'due') || null;
  const noDateColumn = sizes.some(s => s.noDate > 0);
  const flagsOf = s => flagMap.get(String(s.itemId));
  const handlers = { onRecord, onHelp, onClear, onStop };
  return (
    <>
      {notes}
      {next && (
        <NextUp size={next} horizonDays={horizonDays} flags={flagsOf(next)} myId={myId} mayClear={mayClear}
          busyKey={busyKey} {...handlers} />
      )}
      {sizes.length > 0 && (
        <>
          <p className="wp-sum">{summaryLine(sizes, horizonDays)}</p>
          <div className={`wp-dtbl${noDateColumn ? ' nodate' : ''}`}>
            <div className="wp-dtr head">
              <span>Size</span><span className="n">Late</span><span className="n">Today</span>
              <span className="n">{nextLabel(horizonDays)}</span>
              {noDateColumn && <span className="n">No date</span>}
              <span className="n">Later</span><span className="n">Top-up</span><span className="n">Reserve</span>
              <span className="n">Welded, waiting</span><span className="n">Still</span><span />
            </div>
            {sizes.map(size => (
              <DeskRow key={size.itemId} size={size} horizonDays={horizonDays} noDateColumn={noDateColumn}
                flags={flagsOf(size)} myId={myId} mayClear={mayClear} busyKey={busyKey} {...handlers}
                onCount={onCount} countReason={countReason} />
            ))}
          </div>
        </>
      )}
    </>
  );
}
